import React, { useCallback, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import {
  LuBriefcase,
  LuCalendarCheck,
  LuCalendarPlus,
  LuChevronLeft,
  LuChevronRight,
  LuCircleCheck,
  LuFileSpreadsheet,
  LuChartGantt,
} from 'react-icons/lu';
import ToggleChip from '../../../components/data/ToggleChip';
import Button from '../../../components/ui/Button';
import IconButton from '../../../components/ui/IconButton';
import SegmentedControl from '../../../components/ui/SegmentedControl';
import { useUserProfile } from '../../../contexts/UserProfileContext';
import useElementWidth from '../../../hooks/useElementWidth';
import { useTranslation } from '../../../i18n/LanguageContext';
import { mergeByTime } from '../../../utils/calendarSorting';
import { getLeavesForDate } from '../../../utils/employeeLeaves';
import { buildProjectLegend, buildProjectSlotMap, projectSlotOf } from '../../../utils/projectColors';
import { formatCompletionDate } from '../../../utils/visitCompletion';
import VisitModal from '../../OperationManagement/VisitModal';
// Only global theme variables are left in this file; importing it keeps their place in the cascade
import '../PersonalCalendar.module.css';
// Global legacy dark-mode rules (Bootstrap dialogs, bare inputs); kept so legacy screens look as before
import '../PersonalCalendarDarkMode.css';
import { EmptyState, LoadingRows } from '../components/ProfileUi';
import AddVisitDialog from './AddVisitDialog';
import { CalendarHelpButton, ProjectFilterButton } from './CalendarLegend';
import DayAgenda from './DayAgenda';
import { JobDetailDialog, AddJobDialog } from './JobDialogs';
import MonthGrid from './MonthGrid';
import useCalendarData from './useCalendarData';
import CalendarTutorialPlayer from './tutorial/CalendarTutorialPlayer';
import { CalendarTutorialBanner, CalendarTutorialButton, useCalendarTutorial } from './tutorial/CalendarTutorialCta';
import {
  buildMonthItems,
  dateKey,
  isJobOnDay,
  isSameDay,
  isVisitOnDay,
  monthCells,
  normalizeDate,
  visitDayOffset,
  visitDayState,
  visitWindow,
  visitsByDay,
} from './calendarDates';
import { VISIT_STATE_TONES } from './calendarTones';

const MONTH_NAMES_TR = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

// Container widths: below COMPACT the grid shows dots, from DOCKED the selected
// day sits beside the grid
const COMPACT_BELOW = 640;
const DOCKED_FROM = 1040;

/** On/off view preference remembered in this browser (falls back to the default). */
const usePreference = (key, initial) => {
  const [value, setValue] = useState(() => {
    try {
      const stored = window.localStorage.getItem(key);
      return stored === null ? initial : stored === 'true';
    } catch {
      return initial;
    }
  });
  const update = useCallback((next) => {
    setValue(next);
    try {
      window.localStorage.setItem(key, String(next));
    } catch {
      // Tarayıcı depolaması kapalıysa yalnızca bu oturumda geçerli
    }
  }, [key]);
  return [value, update];
};

/**
 * "Takvim": the month of the signed-in employee — visits, personal jobs and
 * approved leave. Each visit is one bar over its window (darker on its visit
 * day) instead of a chip on every day, so a busy month stays readable;
 * windows and completed visits can be hidden. A day lists what is planned on
 * it and, separately, the visits whose window is open. New visits / jobs, the
 * month's Excel list and a per-project filter live here too.
 */
const PersonalCalendar = () => {
  const { t, locale } = useTranslation();
  const label = (key, vars) => t(`profilePage.calendar.${key}`, vars);
  const { employeeId, employeeData } = useUserProfile();
  const { visits, setVisits, fetchVisits, calendarJobs, setCalendarJobs, leaveDayMap, userProjects, activeProjects, loading } = useCalendarData(employeeId);

  const containerRef = useRef(null);
  const agendaRef = useRef(null);
  const width = useElementWidth(containerRef, 1200);
  const compact = width < COMPACT_BELOW;
  const docked = width >= DOCKED_FROM;

  const [currentDate, setCurrentDate] = useState(() => new Date());
  const [selectedDate, setSelectedDate] = useState(() => normalizeDate(new Date()));
  const [view, setView] = useState('month');
  const [hiddenProjects, setHiddenProjects] = useState(() => new Set());
  const [showWindows, setShowWindows] = usePreference('habcare.calendar.showWindows', true);
  const [hideCompleted, setHideCompleted] = usePreference('habcare.calendar.hideCompleted', false);
  const [addVisit, setAddVisit] = useState({ open: false, date: null });
  const [addJob, setAddJob] = useState({ open: false, date: null });
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [showVisitModal, setShowVisitModal] = useState(false);
  // Video rehber: banner until watched, button always
  const tutorial = useCalendarTutorial();

  // Projeye göre renk: her projeye kalıcı bir slot, çubukta sol kenar şeridi
  const projectSlotMap = useMemo(() => buildProjectSlotMap(userProjects), [userProjects]);
  const projectNameById = useMemo(() => {
    const map = new Map();
    userProjects.forEach((project) => map.set(project.id, project.titleShort || project.title || label('projectFallback')));
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProjects]);
  const slotOf = useCallback((projectId) => projectSlotOf(projectSlotMap, projectId), [projectSlotMap]);
  const nameOf = useCallback((projectId) => projectNameById.get(projectId) || '', [projectNameById]);

  const shownVisits = useMemo(() => visits.filter((visit) => !hiddenProjects.has(visit.projectId) && !(hideCompleted && visit.status)), [visits, hiddenProjects, hideCompleted]);
  // Bir projeye bağlı işler de proje filtresine uyar
  const shownJobs = useMemo(() => calendarJobs.filter((job) => !hiddenProjects.has(job.projectId) && !(hideCompleted && job.status)), [calendarJobs, hiddenProjects, hideCompleted]);
  const monthVisitMap = useMemo(() => visitsByDay(shownVisits, currentDate), [shownVisits, currentDate]);
  const monthItems = useMemo(() => buildMonthItems({ visits: shownVisits, jobs: shownJobs, showWindows }), [shownVisits, shownJobs, showWindows]);
  const cells = useMemo(() => monthCells(currentDate), [currentDate]);

  // Bir gün: o güne planlananlar (vizit günü ya da yapıldığı gün + işler) ve
  // o gün penceresi açık olan diğer vizitler
  const dayData = useCallback((date) => {
    const covering = monthVisitMap.get(dateKey(date)) || [];
    const onDay = covering.filter((visit) => isVisitOnDay(visit, date));
    const windowVisits = covering
      .filter((visit) => !isVisitOnDay(visit, date))
      .sort((a, b) => Math.abs(visitDayOffset(a, date)) - Math.abs(visitDayOffset(b, date)));
    return {
      entries: mergeByTime(onDay, shownJobs.filter((job) => isJobOnDay(job, date))),
      windowVisits,
      leaves: getLeavesForDate(leaveDayMap, date),
    };
  }, [monthVisitMap, shownJobs, leaveDayMap]);

  const inShownMonth = (date) => date.getFullYear() === currentDate.getFullYear() && date.getMonth() === currentDate.getMonth();
  const monthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  const monthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

  // Filtrede o ay penceresi takvime düşen projeler ve o aya düşen işlerin
  // projeleri listelenir (gizlenenler dahil)
  const monthProjects = useMemo(() => {
    const visitIds = visits
      .filter((visit) => {
        if (!visit.firstVisitDate) return false;
        const { start, end } = visitWindow(visit);
        return !Number.isNaN(start.getTime()) && start <= monthEnd && end >= monthStart;
      })
      .map((visit) => visit.projectId);
    const jobIds = calendarJobs
      .filter((job) => job.projectId && normalizeDate(new Date(job.startDate)) <= monthEnd && normalizeDate(new Date(job.endDate || job.startDate)) >= monthStart)
      .map((job) => job.projectId);
    return buildProjectLegend([...visitIds, ...jobIds], projectSlotMap, projectNameById);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visits, calendarJobs, currentDate, projectSlotMap, projectNameById]);

  // Ayın özeti: vizitler vizit günündeki durumlarına göre bir kez sayılır
  const summary = useMemo(() => {
    const counts = { total: 0, planned: 0, inProgress: 0, overdue: 0, completed: 0, screenFail: 0, jobs: 0 };
    shownVisits.forEach((visit) => {
      const date = new Date(visit.firstVisitDate);
      if (Number.isNaN(date.getTime()) || !inShownMonth(date)) return;
      counts.total += 1;
      if (visit.screenFail) {
        counts.screenFail += 1;
        return;
      }
      const state = visitDayState(visit, date);
      if (state === 'completedDay' || state === 'windowClosed' || state === 'completed') counts.completed += 1;
      else counts[state] += 1;
    });
    counts.jobs = shownJobs.filter((job) => {
      const start = new Date(job.startDate);
      return !Number.isNaN(start.getTime()) && inShownMonth(start);
    }).length;
    return counts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shownVisits, shownJobs, currentDate]);

  const goToMonth = (offset) => {
    const next = new Date(currentDate.getFullYear(), currentDate.getMonth() + offset, 1);
    setCurrentDate(next);
    // Seçili gün yeni ayın içinde değilse ayın ilk gününe (ya da bugüne) geçilir
    const today = normalizeDate(new Date());
    setSelectedDate(today.getFullYear() === next.getFullYear() && today.getMonth() === next.getMonth() ? today : next);
  };

  const goToToday = () => {
    const today = normalizeDate(new Date());
    setCurrentDate(today);
    setSelectedDate(today);
  };

  const selectDay = (date, { reveal } = {}) => {
    setSelectedDate(normalizeDate(date));
    // "+N daha" ile seçilen gün, liste ızgaranın altındaysa görünür hale getirilir
    if (reveal && !docked) {
      window.requestAnimationFrame(() => agendaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }));
    }
  };

  const openVisit = (visit) => {
    setSelectedVisit(visit);
    setShowVisitModal(true);
  };

  const toggleProject = (projectId) => {
    setHiddenProjects((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) next.delete(projectId);
      else next.add(projectId);
      return next;
    });
  };

  // Gösterilen ayın vizitleri Excel'e (başlıklar ve durumlar Türkçe, eskisi gibi)
  const handleExcelExport = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const projectMap = {};
    userProjects.forEach((p) => {
      projectMap[p.id] = p.titleShort || p.title || p.id;
    });

    const exportStart = new Date(year, month, 1);
    exportStart.setHours(0, 0, 0, 0);
    const exportEnd = new Date(year, month + 1, 0);
    exportEnd.setHours(23, 59, 59, 999);
    const today = normalizeDate(new Date());

    const formatDate = (d) => {
      if (!d) return '';
      const nd = new Date(d);
      return `${nd.getDate().toString().padStart(2, '0')}.${(nd.getMonth() + 1).toString().padStart(2, '0')}.${nd.getFullYear()}`;
    };

    const rows = [];
    visits.forEach((visit) => {
      const { start: startDate, end: endDate, firstVisitDate } = visitWindow(visit);
      if (startDate > exportEnd || endDate < exportStart) return;

      const visitEndDeadline = new Date(endDate);
      visitEndDeadline.setHours(23, 59, 59, 999);
      visitEndDeadline.setTime(visitEndDeadline.getTime() + 72 * 60 * 60 * 1000);

      let durum;
      if (visit.screenFail) durum = 'Screen Fail';
      else if (visit.status) durum = 'Tamamlandı';
      else if (visitEndDeadline < new Date()) durum = 'Tamamlanmadı';
      else if (firstVisitDate > today) durum = 'Planlanan';
      else durum = 'Devam Ediyor';

      rows.push({
        'Hasta ID': visit.patientId || '-',
        'Proje': projectMap[visit.projectId] || visit.projectId || '-',
        'İlk Ziyaret Tarihi': formatDate(visit.firstVisitDate),
        'Pencere Başlangıcı': formatDate(startDate),
        'Pencere Bitişi': formatDate(endDate),
        'Ziyaret Tipi': visit.visitType || '-',
        'Ziyaret Numarası': visit.visitNumber !== undefined && visit.visitNumber !== '' ? visit.visitNumber : '-',
        'Durum': durum,
        'Tamamlanma Tarihi': formatCompletionDate(visit, { withTime: true }) || '-',
        'Screen Fail': visit.screenFail ? 'Evet' : 'Hayır',
        'Notlar': visit.info || '-',
        'Pre-Visit Form': visit.preVisitForm ? 'Evet' : 'Hayır',
      });
    });

    if (rows.length === 0) {
      toast.info(label('excelEmpty'));
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(rows);
    worksheet['!cols'] = Object.keys(rows[0]).map((key) => ({
      wch: Math.max(key.length, ...rows.map((r) => String(r[key] || '').length)) + 2,
    }));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Visitler');
    XLSX.writeFile(workbook, `Visitler_${MONTH_NAMES_TR[month]}_${year}.xlsx`);
  };

  const monthTitle = currentDate.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
  const isCurrentMonth = inShownMonth(new Date());
  const selected = inShownMonth(selectedDate) ? selectedDate : null;
  const selectedData = selected ? dayData(selected) : { entries: [], windowVisits: [], leaves: [] };

  const agendaHandlers = {
    onOpenVisit: openVisit,
    onOpenJob: setSelectedJob,
    onAddVisit: (date) => setAddVisit({ open: true, date }),
    onAddJob: (date) => setAddJob({ open: true, date }),
    projectSlotOf: slotOf,
    projectNameOf: nameOf,
  };

  // Liste görünümü yalnızca planlanan günleri gösterir (pencere günleri değil)
  const listDays = view === 'list'
    ? cells.filter(Boolean).map((date) => ({ date, ...dayData(date) })).filter((day) => day.entries.length > 0 || day.leaves.length > 0)
    : [];

  const summaryItems = [
    { key: 'total', value: summary.total, dot: 'tw-bg-slate-400' },
    { key: 'planned', value: summary.planned, dot: VISIT_STATE_TONES.planned.dot },
    { key: 'inProgress', value: summary.inProgress, dot: VISIT_STATE_TONES.inProgress.dot },
    { key: 'overdue', value: summary.overdue, dot: VISIT_STATE_TONES.overdue.dot },
    { key: 'completed', value: summary.completed, dot: VISIT_STATE_TONES.completedDay.dot, hideZero: hideCompleted },
    { key: 'screenFail', value: summary.screenFail, dot: 'tw-bg-fuchsia-500', hideZero: true },
    { key: 'jobs', value: summary.jobs, dot: 'tw-bg-slate-400', hideZero: true },
  ].filter((item) => !(item.hideZero && item.value === 0));

  return (
    <div ref={containerRef} className="tw-space-y-3.5">
      {tutorial.showBanner && (
        <CalendarTutorialBanner minutes={tutorial.minutes} onWatch={tutorial.open} onDismiss={tutorial.dismiss} />
      )}

      {/* Ay gezinme ve işlemler */}
      <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
        <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-1.5">
          <IconButton label={label('prevMonth')} onClick={() => goToMonth(-1)}>
            <LuChevronLeft aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
          </IconButton>
          <h2 aria-live="polite" className="tw-min-w-[8.5rem] tw-text-center tw-text-lg tw-font-semibold tw-capitalize tw-tracking-tight tw-text-slate-900 sm:tw-min-w-[11rem] sm:tw-text-xl dark:tw-text-white">
            {monthTitle}
          </h2>
          <IconButton label={label('nextMonth')} onClick={() => goToMonth(1)}>
            <LuChevronRight aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
          </IconButton>
          {(!isCurrentMonth || !isSameDay(selectedDate, new Date())) && (
            <Button size="sm" variant="ghost" onClick={goToToday} className="tw-ml-1">
              <LuCalendarCheck aria-hidden="true" className="tw-h-4 tw-w-4" />
              {label('today')}
            </Button>
          )}
        </div>
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
          <SegmentedControl
            size="sm"
            layoutId="profile-calendar-view"
            label={label('viewLabel')}
            value={view}
            onChange={setView}
            options={[{ value: 'month', label: label('views.month') }, { value: 'list', label: label('views.list') }]}
          />
          <Button size="sm" onClick={handleExcelExport} title={label('actions.excelHint')}>
            <LuFileSpreadsheet aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-emerald-600 dark:tw-text-emerald-400" />
            <span className="max-sm:tw-sr-only">{label('actions.excel')}</span>
          </Button>
          <Button size="sm" onClick={() => setAddJob({ open: true, date: selected })}>
            <LuBriefcase aria-hidden="true" className="tw-h-4 tw-w-4" />
            {label('actions.addJob')}
          </Button>
          <Button variant="primary" size="sm" onClick={() => setAddVisit({ open: true, date: selected })}>
            <LuCalendarPlus aria-hidden="true" className="tw-h-4 tw-w-4" />
            {label('actions.addVisit')}
          </Button>
        </div>
      </div>

      {/* Ayın özeti ve görünüm seçenekleri */}
      <div className="tw-flex tw-flex-col tw-gap-2.5 xl:tw-flex-row xl:tw-items-center xl:tw-justify-between">
        <ul aria-label={label('summary.label')} className="tw-flex tw-min-w-0 tw-gap-2 tw-overflow-x-auto tw-pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:tw-hidden">
          {summaryItems.map((item) => (
            <li key={item.key} className="tw-flex tw-shrink-0 tw-items-center tw-gap-2 tw-rounded-full tw-border tw-border-slate-200 tw-bg-white/70 tw-px-3 tw-py-1.5 tw-text-xs dark:tw-border-white/10 dark:tw-bg-white/[0.04]">
              <span aria-hidden="true" className={`tw-h-2 tw-w-2 tw-rounded-full ${item.dot}`} />
              <span className="tw-font-semibold tw-tabular-nums tw-text-slate-900 dark:tw-text-white">{loading ? '…' : item.value}</span>
              <span className="tw-text-slate-500 dark:tw-text-slate-400">{label(`summary.${item.key}`)}</span>
            </li>
          ))}
        </ul>
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
          {view === 'month' && (
            <ToggleChip
              pressed={showWindows}
              onChange={setShowWindows}
              icon={LuChartGantt}
              label={label('options.windows')}
              title={label('options.windowsHint')}
            />
          )}
          <ToggleChip
            pressed={hideCompleted}
            onChange={setHideCompleted}
            icon={LuCircleCheck}
            label={label('options.hideCompleted')}
            title={label('options.hideCompletedHint')}
            tone="amber"
          />
          <ProjectFilterButton
            projects={monthProjects}
            hiddenProjects={hiddenProjects}
            onToggleProject={toggleProject}
            onShowAll={() => setHiddenProjects(new Set())}
          />
          <CalendarHelpButton />
          <CalendarTutorialButton onClick={tutorial.open} watched={tutorial.watched} />
        </div>
      </div>

      {loading && <LoadingRows count={4} height="tw-h-24" label={label('loading')} />}

      {!loading && view === 'month' && (
        <div className={docked ? 'tw-grid tw-items-start tw-gap-4 tw-grid-cols-[minmax(0,1fr)_21rem]' : 'tw-space-y-4'}>
          <MonthGrid
            cells={cells}
            items={monthItems}
            dayData={dayData}
            selectedKey={selected ? dateKey(selected) : null}
            compact={compact}
            showWindows={showWindows}
            onSelectDay={selectDay}
            onOpenVisit={openVisit}
            onOpenJob={setSelectedJob}
            projectSlotOf={slotOf}
            projectNameOf={nameOf}
          />
          {selected && (
            <motion.div
              ref={agendaRef}
              key={dateKey(selected)}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.18 }}
              className={`tw-scroll-mt-52 ${docked ? 'tw-sticky tw-top-[12rem] tw-max-h-[calc(100vh-13rem)] tw-overflow-y-auto tw-overscroll-contain tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white/70 tw-p-3.5 glass-scrollbar dark:tw-border-white/10 dark:tw-bg-white/[0.03]' : ''}`}
            >
              <DayAgenda
                date={selected}
                entries={selectedData.entries}
                windowVisits={selectedData.windowVisits}
                leaves={selectedData.leaves}
                {...agendaHandlers}
              />
            </motion.div>
          )}
        </div>
      )}

      {!loading && view === 'list' && (
        listDays.length === 0 ? (
          <EmptyState icon={LuCalendarCheck} title={label('emptyMonth')} text={label('emptyMonthHint')} />
        ) : (
          <div className="tw-space-y-5">
            {listDays.map((day) => (
              <DayAgenda
                key={dateKey(day.date)}
                date={day.date}
                entries={day.entries}
                leaves={day.leaves}
                showEmpty={false}
                {...agendaHandlers}
              />
            ))}
          </div>
        )
      )}

      <VisitModal
        show={showVisitModal}
        onHide={() => setShowVisitModal(false)}
        visit={selectedVisit}
        onVisitUpdate={(updatedVisit) => {
          if (updatedVisit.deleted) {
            setVisits((prev) => prev.filter((visit) => visit.id !== updatedVisit.id));
          } else {
            setVisits((prev) => prev.map((visit) => (visit.id === updatedVisit.id ? updatedVisit : visit)));
          }
        }}
      />

      <CalendarTutorialPlayer isOpen={tutorial.isOpen} onClose={tutorial.close} onWatched={tutorial.markWatched} />

      <AddVisitDialog
        isOpen={addVisit.open}
        initialDate={addVisit.date}
        employeeId={employeeId}
        employeeData={employeeData}
        userProjects={userProjects}
        visits={visits}
        onClose={() => setAddVisit({ open: false, date: null })}
        onCreated={fetchVisits}
      />

      <AddJobDialog
        isOpen={addJob.open}
        initialDate={addJob.date}
        employeeId={employeeId}
        projects={activeProjects}
        onClose={() => setAddJob({ open: false, date: null })}
        onCreated={(job) => setCalendarJobs((prev) => [...prev, job])}
      />

      <JobDetailDialog
        job={selectedJob}
        projectName={selectedJob?.projectId ? nameOf(selectedJob.projectId) : ''}
        onClose={() => setSelectedJob(null)}
        onUpdated={(job) => {
          setCalendarJobs((prev) => prev.map((item) => (item.id === job.id ? { ...item, status: job.status } : item)));
          setSelectedJob(job);
        }}
        onDeleted={(jobId) => setCalendarJobs((prev) => prev.filter((item) => item.id !== jobId))}
      />
    </div>
  );
};

export default PersonalCalendar;
